import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { intlLocale } from '@/i18n/dateFormat'
import { Ghost, Calendar } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { announcementApi } from '@/api/announcementApi'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { renderAnnouncementMarkdown } from './announcementMarkdown'
import { priorityStyle } from './priorityStyles'
import type { Announcement } from './types'
import type { SnowflakeId } from '@/types/snowflake'

interface Props {
  announcementId: SnowflakeId | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the announcement id when the user clicks "Got it". */
  onRead?: (id: SnowflakeId) => void
}

function pick(base: string | null | undefined, i18n: Record<string, string> | undefined, lng: string): string {
  return (i18n && i18n[lng]) || base || ''
}

function fmtTime(iso: string | null | undefined, tz: string, locale: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(intlLocale(locale), {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    })
  } catch {
    return ''
  }
}

export function AnnouncementDetailModal({ announcementId, open, onOpenChange, onRead }: Props) {
  const { t, i18n } = useTranslation('announcements')
  const lng = i18n.language?.split('-')[0] ?? 'en'
  const tz = useUserTimezone()
  const [loading, setLoading] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [ann, setAnn] = useState<Announcement | null>(null)

  useEffect(() => {
    if (!open || !announcementId) return
    let alive = true
    setLoading(true)
    setNotFound(false)
    setAnn(null)
    announcementApi.detail(announcementId)
      .then((data) => { if (alive) setAnn(data) })
      .catch(() => { if (alive) setNotFound(true) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [open, announcementId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {notFound ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-muted text-muted-foreground">
              <Ghost size={22} />
            </div>
            <h3 className="text-lg font-bold">{t('gone.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('gone.body')}</p>
            <Button variant="outline" className="mt-2 w-full" onClick={() => onOpenChange(false)}>
              {t('close')}
            </Button>
          </div>
        ) : loading || !ann ? (
          <div className="py-10 text-center text-sm text-muted-foreground">{t('loading')}</div>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-1 flex items-center gap-2">
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold uppercase', priorityStyle(ann.priority).header)}>
                  {t(`priority.${ann.priority}`)}
                </span>
                {ann.category ? (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {ann.category}
                  </span>
                ) : null}
              </div>
              <DialogTitle className="text-xl">{pick(ann.title, ann.titleI18n, lng)}</DialogTitle>
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar size={12} /> {fmtTime(ann.publishedAt, tz, i18n.language)}
              </p>
            </DialogHeader>

            <div
              className="prose prose-sm max-h-[55vh] max-w-none overflow-auto"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: renderAnnouncementMarkdown(pick(ann.content, ann.contentI18n, lng)),
              }}
            />

            <DialogFooter>
              <Button
                onClick={() => {
                  if (announcementId) onRead?.(announcementId)
                  onOpenChange(false)
                }}
              >
                {t('gotIt')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
